import React, { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import PropTypes from "prop-types";
import {
  AddPhoneMfaDevice,
  ContextForm,
  phoneFormatter,
  useNotificationContext,
} from "cerulean";
import MfaDevice from "byzantine/src/MfaDevice";
import User from "byzantine/src/User";
import { useFingerPrintData } from "../../../fingerprint";
import { checkAuthSession } from "../../../../src/utils";
import { useTriggerGuideForceReset } from "../../../../src/modules/guides/hooks";
import SmsOptInDisclosure from "../../../../src/modules/smsOptInDisclosure";

const AddNewPhoneDevice = ({
  data,
  devices,
  onCancel,
  onChange,
  onDone,
  setDevices,
}) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const [user, setUser] = useState(null);
  const [consented, setConsented] = useState(false);
  const { deviceId, deviceRequestId } = useFingerPrintData("add_mfa");
  const triggerGuideForceReset = useTriggerGuideForceReset();
  useEffect(() => {
    User.getUser().then((u) => {
      setUser(u);
    });
  }, []);

  const onRequestCode = () => {
    onChange({
      twoFactorAuthentication: {
        phone_number: data.twoFactorAuthentication.phone_number,
      },
    });
    return user.addMfaDevice({
      phone_number: data.twoFactorAuthentication.phone_number,
      device_type: MfaDevice.TYPES.PHONE,
    });
  };

  const onVerifyCode = async (seed) => {
    const newDevice = await user.addMfaDevice({
      phone_number: data.twoFactorAuthentication.phone_number,
      device_type: MfaDevice.TYPES.PHONE,
      token: data.twoFactorAuthentication.token,
      seed,
      device_id: deviceId,
      device_request_id: deviceRequestId,
    });
    let action;
    if (devices.some((device) => device.id === newDevice?.device?.id)) {
      action = "reverified";
    } else {
      setDevices([...devices, newDevice.device]);
      action = "added";
    }
    onDone();
    if (action === "added") {
      triggerGuideForceReset();
    }
    sendNotification({
      type: "success",
      text:
        action === "added"
          ? l10n.getString("notification-phone-device-added", {
              phoneNumber: phoneFormatter(
                data.twoFactorAuthentication.phone_number
              ),
            })
          : l10n.getString("notification-phone-device-reverified", {
              phoneNumber: phoneFormatter(
                data.twoFactorAuthentication.phone_number
              ),
            }),
    });
  };

  return (
    <div style={{ fontSize: "16px" }}>
      <div className="margin--top--m fontWeight--bold margin--bottom--s">
        {l10n.getString("heading-add-phone-device")}
      </div>
      <ContextForm data={data} onChange={onChange}>
        <ContextForm.Field field="twoFactorAuthentication">
          <AddPhoneMfaDevice
            field="twoFactorAuthentication"
            onCancel={onCancel}
            onRequestCode={onRequestCode}
            onUseADifferentPhoneNumber={() =>
              onChange({ twoFactorAuthentication: {} })
            }
            onVerifyCode={onVerifyCode}
            callback={() =>
              checkAuthSession(
                l10n.getString("logout-message-blocked-and-locked")
              )
            }
            submitDisabled={!consented}
            consentSlot={
              <div className="margin--top--s">
                <SmsOptInDisclosure
                  consented={consented}
                  onConsentChange={setConsented}
                />
              </div>
            }
          />
        </ContextForm.Field>
      </ContextForm>
    </div>
  );
};
AddNewPhoneDevice.propTypes = {
  data: PropTypes.object.isRequired,
  devices: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  setDevices: PropTypes.func.isRequired,
};

export default AddNewPhoneDevice;
